export interface ChatChartFilterCondition {
  field: string;
  operator: 'eq' | 'gt' | 'lt' | 'gte' | 'lte' | 'in' | 'contains';
  value: string | number | boolean | string[];
}

export interface ChatChartDrilldown {
  entity: 'shipment' | 'alert';
  filters: ChatChartFilterCondition[];
}

export interface ChatChartPoint {
  value: number;
  // Omitted for a point that isn't grounded in one traceable query (e.g. a
  // derived average) -- that point just isn't clickable.
  drilldown?: ChatChartDrilldown;
}

// A plain number is the legacy shape -- the older, non-AgentCore chatbot's
// fenced-JSON charts still emit this and are never migrated to drilldown;
// chartPointValue/chartPointDrilldown below treat both uniformly so
// ChatMessageChart doesn't need to know which chatbot produced a spec.
export type ChatChartDataPoint = number | ChatChartPoint;

export function chartPointValue(point: ChatChartDataPoint): number {
  return typeof point === 'number' ? point : point.value;
}

export function chartPointDrilldown(
  point: ChatChartDataPoint
): ChatChartDrilldown | undefined {
  return typeof point === 'number' ? undefined : point.drilldown;
}

export interface ChatChartSeries {
  name: string;
  data: ChatChartDataPoint[];
}

export type ChatChartType =
  | 'bar'
  | 'horizontal_bar'
  | 'stacked_bar'
  | 'line'
  | 'area'
  | 'donut'
  // The default breakdown chart — a plain solid pie, no hole. "donut" is
  // the opt-in alternate style, used only when the user explicitly asks
  // for a donut or a different pie style.
  | 'pie';

export interface ChatChartSpec {
  type: ChatChartType;
  title?: string;
  categories: string[];
  series: ChatChartSeries[];
}

export type ChatMessagePart =
  { kind: 'text'; text: string } | { kind: 'chart'; spec: ChatChartSpec };

const CHART_FENCE_RE = /```chart\s*([\s\S]*?)```/g;
// A ```chart fence with no closing ``` yet — still streaming in. Showing the
// raw JSON mid-stream looks broken, so this trailing fragment is hidden until
// it closes and can be parsed as a whole block. Only the older,
// non-AgentCore chatbot still emits charts this way (see parseChatMessageParts'
// docstring) -- the AgentCore path renders `chart` SSE frames directly, never
// embedded in message text.
const DANGLING_FENCE_RE = /```chart[\s\S]*$/;

// Types whose job is a single part-to-whole breakdown — past this many
// slices a donut/pie is unreadable (dataviz skill anti-pattern), so the
// parser demotes an oversized one to horizontal_bar instead of rendering it
// as-is. Defense-in-depth: the prompt is told the same cap, this just covers
// the turns where it doesn't hold.
const SINGLE_SERIES_TYPES = new Set<ChatChartType>(['donut', 'pie', 'area']);
// stacked_bar's whole job is showing how a mix splits across 2+ series — one
// series has nothing to stack against, and would otherwise sneak past
// validation into a chart config that sets both `stacked` and `distributed`
// at once, a combination the bar engine doesn't support.
const MULTI_SERIES_REQUIRED_TYPES = new Set<ChatChartType>(['stacked_bar']);
const MAX_DONUT_CATEGORIES = 8;
const MIN_DONUT_CATEGORIES = 3;

const FILTER_OPERATORS: ReadonlySet<string> = new Set([
  'eq',
  'gt',
  'lt',
  'gte',
  'lte',
  'in',
  'contains',
]);

function isChatChartFilterCondition(
  value: unknown
): value is ChatChartFilterCondition {
  if (!value || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  if (typeof f.field !== 'string' || !f.field) return false;
  if (typeof f.operator !== 'string' || !FILTER_OPERATORS.has(f.operator)) {
    return false;
  }
  const t = typeof f.value;
  if (t === 'string' || t === 'number' || t === 'boolean') return true;
  return Array.isArray(f.value) && f.value.every((v) => typeof v === 'string');
}

function isChatChartDrilldown(value: unknown): value is ChatChartDrilldown {
  if (!value || typeof value !== 'object') return false;
  const d = value as Record<string, unknown>;
  if (d.entity !== 'shipment' && d.entity !== 'alert') return false;
  return (
    Array.isArray(d.filters) && d.filters.every(isChatChartFilterCondition)
  );
}

function isChatChartDataPoint(value: unknown): value is ChatChartDataPoint {
  if (typeof value === 'number') return true;
  if (!value || typeof value !== 'object') return false;
  const p = value as Record<string, unknown>;
  if (typeof p.value !== 'number') return false;
  return p.drilldown === undefined || isChatChartDrilldown(p.drilldown);
}

function isChatChartSeries(value: unknown): value is ChatChartSeries {
  if (!value || typeof value !== 'object') return false;
  const series = value as Record<string, unknown>;
  return (
    typeof series.name === 'string' &&
    Array.isArray(series.data) &&
    series.data.every(isChatChartDataPoint)
  );
}

const CHART_TYPES: ReadonlySet<string> = new Set([
  'bar',
  'horizontal_bar',
  'stacked_bar',
  'line',
  'area',
  'donut',
  'pie',
]);

function isChatChartSpec(value: unknown): value is ChatChartSpec {
  if (!value || typeof value !== 'object') return false;
  const spec = value as Record<string, unknown>;
  if (typeof spec.type !== 'string' || !CHART_TYPES.has(spec.type)) {
    return false;
  }
  if (
    !Array.isArray(spec.categories) ||
    !spec.categories.every((c) => typeof c === 'string')
  ) {
    return false;
  }
  const categories = spec.categories as string[];
  // A single category/value is never a chart — it's a number, and the
  // prompt is told to say so in prose instead (dataviz skill anti-pattern:
  // "a one-bar bar chart, or a 2-slice pie" — the one-category case applies
  // to every chart type, not just pie/donut).
  if (categories.length < 2) return false;

  if (!Array.isArray(spec.series) || spec.series.length === 0) return false;
  if (!spec.series.every(isChatChartSeries)) return false;
  const series = spec.series as ChatChartSeries[];
  if (!series.every((s) => s.data.length === categories.length)) return false;

  const type = spec.type as ChatChartType;
  if (SINGLE_SERIES_TYPES.has(type) && series.length !== 1) return false;
  if (MULTI_SERIES_REQUIRED_TYPES.has(type) && series.length < 2) return false;
  return true;
}

/** Two code-level corrections for the pie/donut anti-patterns the prompt is
 * told to avoid, kept as a backstop for the turns it doesn't hold:
 * - more than eight slices is unreadable → horizontal_bar on the same data
 * - exactly two slices is better as two numbers or a direct comparison →
 *   "bar" (the prompt's own stated alternative), not horizontal_bar, which
 *   is for many/long categories, not two
 * Both reuse the same categories/series envelope, so no data is reshaped. */
function applyChartGuardrails(spec: ChatChartSpec): ChatChartSpec {
  const isPieOrDonut = spec.type === 'donut' || spec.type === 'pie';
  if (!isPieOrDonut) return spec;
  if (spec.categories.length > MAX_DONUT_CATEGORIES) {
    return { ...spec, type: 'horizontal_bar' };
  }
  if (spec.categories.length < MIN_DONUT_CATEGORIES) {
    return { ...spec, type: 'bar' };
  }
  return spec;
}

/** Splits an assistant message into text/chart segments, so text renders
 * through the existing markdown-to-HTML path unchanged and a ```chart``` block
 * renders as a chart instead. Only the older, non-AgentCore chatbot still
 * produces charts this way -- the AgentCore path renders `chart` SSE frames
 * directly (see index.tsx), never embedded in message text, so this is a
 * legacy-compatibility path, not the primary one. Malformed chart JSON falls
 * back to literal text rather than being silently dropped. */
export function parseChatMessageParts(content: string): ChatMessagePart[] {
  const parts: ChatMessagePart[] = [];
  let lastIndex = 0;
  CHART_FENCE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = CHART_FENCE_RE.exec(content)) !== null) {
    const before = content.slice(lastIndex, match.index);
    if (before) parts.push({ kind: 'text', text: before });

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(match[1].trim());
    } catch {
      parsed = null;
    }

    if (isChatChartSpec(parsed)) {
      parts.push({ kind: 'chart', spec: applyChartGuardrails(parsed) });
    } else {
      parts.push({ kind: 'text', text: match[0] });
    }
    lastIndex = CHART_FENCE_RE.lastIndex;
  }

  const tail = content.slice(lastIndex).replace(DANGLING_FENCE_RE, '');
  if (tail) parts.push({ kind: 'text', text: tail });

  return parts;
}
